'use client';
import BadgeWithTooltip, {
    EBadgeWithTooltip,
} from '@/components/shared/badege-with-tooltip/badge-with-tooltip.component';

import Typography from '@/components/shared/typography/typography.component';

import Link from '@/components/WrappedLink';
import { useAnalytics } from '@/integrations';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { isValidUUid } from '@/utils/uuidV4.util';
import { CarListingData } from '@/types/cars.types';
import {
    CalendarCardIcon,
    generalHighlights,
    imgPlaceholder,
    LimitedTimeOffer,
    Speedometer,
    TransmissionIcon,
} from '../../../../public/Images';
import myImageLoader from '@/image.local.loader';
import {
    AVAILABLE_LANGUAGES,
    colors,
    DEFAULT_LANGUAGE,
    FEATURE_FLAG_SHOW_NEW_LISTING,
} from '@/constants';
import { slugify } from '@/utils/filterUtil';
import { InfoOutlinedIcon } from '@/components/shared/car-details-card/reserved-tooltip.component';
import { formatNumber, numberWithCommas } from '@/utils';
import { memo, useMemo } from 'react';
import { ITag } from '@/types/cars.types';
import CarDetailsSectionSeparator from '@/components/car-details/CarProfileNew/CarProfileInfo/CarDetailsSectionSeparator';
import useTranslationHook from '@/hook/use-translation-client.hook';
import { Trans } from 'react-i18next';
import CarDisplayCardClientV2 from '../car-details-card-v2/car-details-car-client.component';
import { getCarType } from '@/components/car-details-v2/car-type';
import { getCategoryBadgeConfig } from '@/components/car-details/CarProfileNew/constants';

interface TFlapperLineProps {
    hasDiscount: boolean;
    discountText: string;
    className?: string;
    tags?: ITag[];
    lang?: AVAILABLE_LANGUAGES;
}

interface TFlapperLineInnerContentProps {
    animationClass: string;
    renderTags: { tag: string; icon: string }[];
}
const FlapperLineInnerContent = ({
    animationClass = '',
    renderTags,
}: TFlapperLineInnerContentProps) => {
    return (
        <div
            className={`absolute top-0 right-0 en:left-0 flex flex-col  ${animationClass} `}
        >
            {renderTags.map(tag => (
                <div
                    className={'w-full flex mx-2 gap-1'}
                    key={`${tag.tag}-${animationClass}`}
                >
                    <img
                        src={myImageLoader({
                            src: tag.icon,
                            width: 14,
                            height: 14,
                            quality: 50,
                        })}
                        alt={tag.tag}
                        width={14}
                        height={14}
                        loading={'lazy'}
                        fetchPriority={'low'}
                        className={' pb-0.5 ar:-scale-x-100'}
                    />
                    <Typography
                        className={
                            'overflow-hidden text-xs leading-[28px] h-[28px]'
                        }
                    >
                        {tag.tag}
                    </Typography>
                </div>
            ))}
        </div>
    );
};
export const FlapperLine = ({
    hasDiscount = false,
    discountText,
    className,
    tags = [],
    lang,
}: TFlapperLineProps) => {
    const tagsToRender: any = [
        hasDiscount ? { tag: discountText, icon: LimitedTimeOffer.src } : null,
        ...tags.map(t => ({
            tag: lang === AVAILABLE_LANGUAGES.ar ? t.nameAr : t.nameEn,
            icon: generalHighlights.src,
        })),
    ]
        .filter(Boolean)
        .slice(0, 2);

    return hasDiscount || (tags && tags.length) ? (
        <div
            className={`relative w-full overflow-hidden h-[28px] ${className}`}
        >
            <FlapperLineInnerContent
                renderTags={tagsToRender}
                animationClass={
                    tagsToRender.length == 1 ? '' : ' animate-twoLineFlap1 '
                }
            />
            <FlapperLineInnerContent
                renderTags={tagsToRender}
                animationClass={
                    tagsToRender.length == 1 ? '' : 'animate-twoLineFlap2'
                }
            />
        </div>
    ) : (
        <span></span>
    );
};

const CarDisplayCardClient = memo(
    ({
        id,
        cardTitle,
        carName,
        installmentPrice,
        totalPrice,
        kilometers,
        year,
        transmission,
        images,
        trim,
        isReserved,
        isPurchaseInProgress,
        make,
        model,
        sourceScreen,
        appliedFilter,
        appliedSort,
        position,
        discountPrice,
        isNewlyAdded,
        useNextImage = false,
        carCardLocalizations,
        tags,
        lang = AVAILABLE_LANGUAGES[DEFAULT_LANGUAGE],
        carData,
    }: {
        id: string;
        cardTitle: string;
        carName: string;
        installmentPrice: number | string;
        totalPrice: number | string;
        kilometers: number | string;
        year: number;
        transmission: string;
        images: string[];
        trim: { ar: string; en: string };
        isReserved: boolean;
        isPurchaseInProgress: boolean;
        make: string;
        model: string;
        sourceScreen: string;
        appliedFilter: boolean;
        appliedSort: boolean;
        position: number;
        discountPrice: number;
        isNewlyAdded: boolean;
        useNextImage?: boolean;
        carCardLocalizations: Record<string, string>;
        tags: ITag[];
        lang: AVAILABLE_LANGUAGES;
        carData: CarListingData;
    }) => {
        const IS_RTL = lang === AVAILABLE_LANGUAGES.ar;
        const router = useRouter();
        const { trackEvent } = useAnalytics();
        const showNewListing = FEATURE_FLAG_SHOW_NEW_LISTING;
        const { t } = useTranslationHook();
        const carType = getCarType(carData);

        const badgeConfig = useMemo(
            () => getCategoryBadgeConfig(carType, t),
            [carType, t]
        );

        if (showNewListing) {
            return (
                <CarDisplayCardClientV2
                    carData={carData}
                    key={carData.id}
                    sourceScreen={sourceScreen}
                    appliedFilter={appliedFilter}
                    appliedSort={appliedSort}
                    position={position}
                />
            );
        }

        const mainPrice = {
            color: '#009B5D',
            lineHeight: '32px',
            opacity: 1,
        };
        const mainPriceWithDsicount = {
            // textDecoration: 'line-through',
            fontSize: '0.75rem',
        };

        function goToCarPage() {
            trackEvent('Action: Click on Car Card', {
                carName,
                make,
                model,
                year,
                trim,
                kilometers,
                price: totalPrice,
                monthlyInstallment: installmentPrice,
                status: isPurchaseInProgress
                    ? 'Being Sold'
                    : isReserved
                    ? 'Reserved'
                    : 'Not Reserved',
                transmission,
                sourceScreen,
                appliedFilter,
                appliedSort,
                position,
                newlyAdded:
                    isNewlyAdded && !(isReserved || isPurchaseInProgress),
                carLink: `${window.location.origin}/${lang}/car-details/${id}`,
            });
            if (isValidUUid(id)) {
                router.push(`/en/car-details/${id}`);
            }
        }

        const hasDiscount = discountPrice !== undefined && discountPrice !== 0;
        const differencs = Math.trunc(
            parseFloat(totalPrice as string) - discountPrice
        );

        const discountPercentage = formatNumber({ value: differencs });

        const RefurbishedBadge = () => {
            return (
                <div
                    className={`absolute z-[2] top-2 flex gap-2 ${
                        IS_RTL ? 'right-2' : 'left-2'
                    }`}
                >
                    {/* Car Type Badge (Certified/Lite/Dealer) */}
                    <div
                        className={
                            'px-2 py-1 rounded-md text-xs font-semibold text-white flex-row flex gap-1 items-center h-[25px]'
                        }
                        style={{
                            backgroundColor: badgeConfig.bgColor,
                        }}
                    >
                        <img
                            src={myImageLoader({
                                src: badgeConfig.icon,
                                width: 20,
                                height: 20,
                            })}
                            alt={`Sylndr ${badgeConfig.label}`}
                        />
                        <Typography
                            variant="captionBold"
                            color={badgeConfig.textColor}
                        >
                            {badgeConfig.label}
                        </Typography>
                    </div>

                    {/* Car Status Badge next to Lite/Certified Badge */}
                    {(isPurchaseInProgress || isReserved) && (
                        <BadgeWithTooltip
                            translations={carCardLocalizations}
                            type={
                                isPurchaseInProgress
                                    ? EBadgeWithTooltip.PURCHASE_IN_PROGRESS
                                    : EBadgeWithTooltip.RESERVED
                            }
                        />
                    )}

                    {/* Newly Added Badge - only show if newly added and not reserved/purchase in progress */}
                    {isNewlyAdded && !(isPurchaseInProgress || isReserved) && (
                        <div
                            className={
                                'px-2 py-1 rounded-md text-xs font-semibold text-white flex-row flex gap-1 items-center h-[25px]'
                            }
                            style={{
                                backgroundColor: '#F79009',
                            }}
                        >
                            {t('Newly Added')}
                        </div>
                    )}
                </div>
            );
        };
        return (
            isValidUUid(id) && (
                <Link
                    prefetch={false}
                    className={`link-car-card ${
                        hasDiscount ? 'has-discount' : 'no-discount'
                    }`}
                    href={`/${lang}/car-details/${slugify(make)}/${slugify(
                        model
                    )}/${id}`}
                    onClick={() => goToCarPage()}
                >
                    <RefurbishedBadge />
                    <div
                        className={
                            ' aspect-[4/3] h-[calc(((100vw_-_32px)_/_4*_3))] md:h-auto w-full rounded-lg bg-contain '
                        }
                        style={{
                            backgroundImage: `url(${imgPlaceholder.src})`,
                        }}
                    >
                        {useNextImage ? (
                            <div
                                key={slugify(images[0])}
                                className={'w-full h-full bg-contain'}
                            >
                                <Image
                                    className={
                                        'w-full h-full max-h-full object-cover rounded-xl rounded-b-none'
                                    }
                                    width={320}
                                    height={240}
                                    quality={100}
                                    src={images[0]}
                                    alt={cardTitle}
                                    placeholder={'empty'}
                                    loading={'eager'}
                                    priority={true}
                                    fetchPriority={'high'}
                                />
                            </div>
                        ) : (
                            <div
                                key={slugify(images[0])}
                                className={'w-full h-full bg-contain'}
                            >
                                <img
                                    className={
                                        'w-full h-full max-h-full object-cover rounded-xl rounded-b-none'
                                    }
                                    width={320}
                                    height={240}
                                    src={myImageLoader({
                                        src: images[0],
                                        width: 320 * 2,
                                        height: 240 * 2,
                                        quality: 100,
                                    })}
                                    alt={cardTitle}
                                    loading={'eager'}
                                    fetchPriority={'high'}
                                />
                            </div>
                        )}
                    </div>
                    <FlapperLine
                        className="bg-secondary-50"
                        hasDiscount={hasDiscount}
                        discountText={carCardLocalizations['limitedOffer']}
                        tags={tags}
                        lang={lang}
                    />

                    <div className={'car-card-container '}>
                        <div className="flex items-center gap-2 mb-1">
                            <Typography
                                className={'car-card-title'}
                                variant={'body1Medium'}
                                component="h2"
                            >
                                {cardTitle}
                            </Typography>
                        </div>
                        <div className="flex flex-col gap-0.5 mb-1">
                            <Typography
                                className={'text-gray-500'}
                                variant={'body2'}
                            >
                                {IS_RTL ? trim?.ar : trim?.en}
                            </Typography>
                            <div className="flex items-center text-gray-500 md:hidden">
                                <Typography variant="body2">
                                    {t('kilometersWithValue', {
                                        value: numberWithCommas(kilometers),
                                    })}
                                </Typography>
                                <CarDetailsSectionSeparator />
                                <Typography variant="body2">
                                    {t(transmission)}
                                </Typography>
                            </div>
                        </div>
                        {/* price details part */}
                        <div className={'car-card-info-container'}>
                            <div style={{ display: 'flex' }}>
                                <Trans
                                    i18nKey="carPriceValue"
                                    values={{
                                        value: numberWithCommas(totalPrice),
                                    }}
                                    components={{
                                        value: (
                                            <Typography
                                                variant="caption"
                                                className={
                                                    lang === 'ar'
                                                        ? 'strikethroughAr'
                                                        : 'strikethrough'
                                                }
                                                style={{
                                                    fontWeight: 600,
                                                    marginInline: '4px',
                                                    fontSize: '1rem',
                                                    color: '#B54708',
                                                }}
                                            />
                                        ),
                                        currency: (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: '#B54708',
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    backgroundColor: '#FFFAEB',
                                    padding: ' 4px 10px 4px 10px',
                                    borderRadius: '24px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    width: 'max-content',
                                    margin: '4px',
                                }}
                            >
                                <Typography
                                    style={
                                        hasDiscount
                                            ? {
                                                  ...mainPrice,
                                                  ...mainPriceWithDsicount,
                                                  lineHeight: '1.25rem',
                                                  color: '#B54708',
                                              }
                                            : mainPrice
                                    }
                                    variant={'subtitle1SemiBold'}
                                >
                                    {`${discountPercentage}`}
                                </Typography>
                            </div>
                        </div>
                        <div
                            className={'flex items-baseline'}
                            style={{
                                justifyContent: 'space-between',
                                marginTop: 'auto',
                            }}
                        >
                            <div className={'flex items-baseline'}>
                                <div
                                    className={
                                        'has-discount:flex no-discount:hidden items-baseline'
                                    }
                                >
                                    <Trans
                                        i18nKey="carPriceValue"
                                        values={{
                                            value: numberWithCommas(
                                                discountPrice
                                            ),
                                        }}
                                        components={{
                                            currency: (
                                                <Typography
                                                    color="#667085"
                                                    variant="caption"
                                                />
                                            ),
                                            value: (
                                                <Typography
                                                    className="text-primary-600 leading-8 opacity-100"
                                                    style={{
                                                        marginInline: '4px',
                                                    }}
                                                    variant="subtitle1SemiBold"
                                                />
                                            ),
                                        }}
                                    />
                                </div>
                                <div
                                    className={
                                        'no-discount:flex has-discount:hidden items-baseline'
                                    }
                                >
                                    <Trans
                                        i18nKey="carPriceValue"
                                        values={{
                                            value: numberWithCommas(totalPrice),
                                        }}
                                        components={{
                                            currency: (
                                                <Typography
                                                    color="#667085"
                                                    variant="caption"
                                                />
                                            ),
                                            value: (
                                                <Typography
                                                    color="#009B5D"
                                                    style={{
                                                        marginInline: '4px',
                                                    }}
                                                    variant="subtitle1SemiBold"
                                                />
                                            ),
                                        }}
                                    />
                                </div>
                            </div>
                            {!!installmentPrice && (
                                <Typography
                                    color="#002D5D"
                                    variant={'body2SemiBold'}
                                >
                                    {carCardLocalizations['from']}{' '}
                                    {numberWithCommas(installmentPrice)}
                                    {carCardLocalizations['/month']}
                                </Typography>
                            )}
                        </div>
                        {/* more details part */}

                        <div
                            className={
                                'hidden items-center flex-wrap gap-2 mt-2 md:flex'
                            }
                        >
                            <div
                                className={'flex gap-4  items-center lg:gap-1'}
                            >
                                <img
                                    width={20}
                                    height={20}
                                    src={myImageLoader({
                                        src: Speedometer.src,
                                        width: 20,
                                        height: 20,
                                    })}
                                    className={'object-contain'}
                                    alt="kilometers"
                                />

                                <Typography color="#667085" variant={'body2'}>
                                    {numberWithCommas(kilometers)}{' '}
                                    {carCardLocalizations['km']}
                                </Typography>
                            </div>
                            <div
                                className={'flex gap-4  items-center lg:gap-1'}
                            >
                                <img
                                    width={20}
                                    height={20}
                                    src={myImageLoader({
                                        src: CalendarCardIcon.src,
                                        width: 20,
                                        height: 20,
                                    })}
                                    alt="year"
                                    className={'object-contain'}
                                />
                                <Typography color="#667085" variant={'body2'}>
                                    {year}
                                </Typography>
                            </div>
                            <div
                                className={'flex gap-4  items-center lg:gap-1'}
                            >
                                <img
                                    width={20}
                                    height={20}
                                    src={myImageLoader({
                                        src: TransmissionIcon.src,
                                        width: 20,
                                        height: 20,
                                    })}
                                    className={'object-contain'}
                                    alt="transmission"
                                />
                                <Typography color="#667085" variant={'body2'}>
                                    {transmission === 'automatic'
                                        ? carCardLocalizations['automatic']
                                        : carCardLocalizations['manual']}
                                </Typography>
                            </div>
                        </div>

                        {/* reserve info on mobile */}
                        {(isPurchaseInProgress || isReserved) && (
                            <div
                                className={`flex flex-row items-start gap-1.5 justify-center mt-2.5 p-2 rounded-lg  md:hidden ${
                                    isPurchaseInProgress && ' bg-blue-dark-25'
                                }
                            ${isReserved && ' bg-gray-100'}
                            `}
                            >
                                <InfoOutlinedIcon
                                    fontSize={'small'}
                                    color={'secondary'}
                                />
                                <Typography
                                    color={
                                        isPurchaseInProgress
                                            ? colors['secondary']
                                            : colors['gray-600']
                                    }
                                    style={{ textAlign: 'start' }}
                                    variant="captionMedium"
                                >
                                    {isPurchaseInProgress
                                        ? carCardLocalizations[
                                              'purchaseInProgress.descriptionLong'
                                          ]
                                        : carCardLocalizations[
                                              'reservedBadgeTooltipDescription'
                                          ]}
                                </Typography>
                            </div>
                        )}
                    </div>
                </Link>
            )
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.id === nextProps.id &&
            prevProps.isNewlyAdded === nextProps.isNewlyAdded &&
            prevProps.isPurchaseInProgress === nextProps.isPurchaseInProgress &&
            prevProps.isReserved === nextProps.isReserved &&
            prevProps.discountPrice === nextProps.discountPrice &&
            prevProps.totalPrice === nextProps.totalPrice &&
            prevProps.appliedFilter === nextProps.appliedFilter &&
            prevProps.appliedSort === nextProps.appliedSort &&
            prevProps.position === nextProps.position
        );
    }
);
CarDisplayCardClient.displayName = 'CarDisplayCardClient';
export default CarDisplayCardClient;
